import { useCallback, useEffect, useState } from 'react';
import {
  Link,
  NavLink,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import Logo from '../assets/images/logo.png';
import { ReactComponent as LogoIcon } from '../assets/images/logoIcon.svg';
import { Dropdown } from 'react-bootstrap';
import { navLinks } from 'constants/paths';
import { pagination } from 'constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from '../redux/store';
import { useGetProjectsMutation } from 'redux/services/project';
import {
  Project,
  saveProjects,
  setDashboardProject,
  setFetchProjectsInProgress,
} from 'redux/projectSlice';
import { useTranslation } from 'react-i18next';
import {
  CasesOutlined,
  CasesSharp,
  DashboardSharp,
  PeopleAltSharp,
  RemoveRedEyeSharp,
  TaskAltSharp,
  WarningAmberSharp,
  ZoomInSharp,
} from '@mui/icons-material';

import './Sidebar.scss';
import { Tooltip } from '@mui/material';

interface SidebarProps {
  className: string;
  active: boolean;
  sidebarHandler: () => void;
}
interface SidebarLinks {
  name: string;
  path: string;
  icon: any;
}

const Sidebar = ({ className, active, sidebarHandler }: SidebarProps) => {
  const { t } = useTranslation();
  const projects = useSelector((state: RootStates) => state.project.projects);
  const location = useLocation();
  const { fetchInProgress, projectFetched } = useSelector(
    (state: RootStates) => state.project,
  );
  const [fetchProjects] = useGetProjectsMutation();
  const dispatch = useDispatch();
  const { projectId: pId, setupId } = useParams();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const projectId = pId || setupId || '';

  const sidebarLinks = [
    {
      name: t('title_home'),
      path: '/dashboard',
      icon: <DashboardSharp />,
    },
    {
      name: t('header_projects'),
      path: navLinks.projects,
      icon: <CasesSharp width="16" />,
    },
    {
      name: t('checklists'),
      path: navLinks.checklist,
      icon: <TaskAltSharp />,
    },
    {
      name: t('header_users'),
      path:
        navLinks.users + `?page=${pagination.page}&limit=${pagination.limit}`,
      icon: <PeopleAltSharp />,
    },
  ];

  useEffect(() => {
    if (projects?.length > 0) {
      const p = projects.find((p) => p.projectInfo.id === projectId);

      if (p) {
        setSelectedProject(p);
        dispatch(setDashboardProject(p));
      } else {
        setSelectedProject(null);
      }
    }
  }, [dispatch, projectId, projects]);

  const handleFetchProject = useCallback(async () => {
    dispatch(setFetchProjectsInProgress());
    const payload: any = await fetchProjects('');
    dispatch(saveProjects(payload.data || []));
  }, [dispatch, fetchProjects]);

  useEffect(() => {
    if (!fetchInProgress && !projectFetched) {
      handleFetchProject();
    }
  }, [fetchInProgress, handleFetchProject, projectFetched]);

  const getProjectClickHandler = (project: any) => () => {
    navigate(`/project/${project.projectInfo.id}`);
  };

  const handleDropdownClick = () => {
    if (!active) {
      sidebarHandler();
    }
  };

  const handleItemClick = () => {
    if (active) {
      sidebarHandler();
    }
  };

  return (
    <aside
      className={`sidebar bg-white overflow-y-auto ${className} ${
        active ? 'show' : 'hide'
      }`}
    >
      <div className="sidebar">
        <div className="d-block p-3">
          {!active && window.innerWidth > 992 ? (
            <LogoIcon style={{ height: 57, width: 40 }} />
          ) : (
            <img
              alt="logo"
              src={Logo}
              style={{ width: '100%', height: '68px' }}
            />
          )}
        </div>
        <ul className="ps-0 list-style-none">
          {sidebarLinks.map((item: SidebarLinks) => (
            <Tooltip key={item.name} title={item.name} arrow placement="right">
              <li
                className="text-start d-flex align-items-center"
                onClick={handleItemClick}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    isActive ? 'active w-100 px-3' : 'w-100 px-3'
                  }
                >
                  {item.icon}
                  <span className="ms-2 menu-item-p">{item.name}</span>
                </NavLink>
              </li>
            </Tooltip>
          ))}
          <Dropdown onClick={handleDropdownClick}>
            <Dropdown.Toggle className="shadow-none w-100 text-left">
              <CasesOutlined style={{ paddingRight: 5 }} color="inherit" />
              <span>
                {selectedProject?.projectInfo?.projectName ??
                  t('label_select_project')}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu">
              {projects
                ?.filter((project: Project) => project.isLaunched)
                .map((project: any) => (
                  <Dropdown.Item
                    className="px-2 project-dropdowns"
                    key={project.projectInfo.id}
                    onClick={getProjectClickHandler(project)}
                  >
                    {project?.projectInfo?.projectName}
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </ul>
        {!!selectedProject && (
          <ul className="project-submenu ps-0 list-style-none">
            <Tooltip title={t('title_inspection')} arrow placement="right">
              <li
                className="text-start d-flex align-items-center"
                onClick={handleItemClick}
              >
                <Link
                  className={`dropdown-item px-4 menu-item-p ${
                    location.pathname?.split('/')?.[3] === 'inspection'
                      ? 'active'
                      : ''
                  }`}
                  to={`/project/${projectId}/inspection`}
                >
                  <ZoomInSharp color="inherit" className="opacity-50" />
                  <span className="mx-3">{t('title_inspection')}</span>
                </Link>
              </li>
            </Tooltip>
            <Tooltip title={t('title_observation')} arrow placement="right">
              <li
                className="text-start d-flex align-items-center"
                onClick={handleItemClick}
              >
                <Link
                  className={`dropdown-item px-4 menu-item-p ${
                    location.pathname?.split('/')?.[3] === 'observation'
                      ? 'active'
                      : ''
                  }`}
                  to={`/project/${projectId}/observation`}
                >
                  <RemoveRedEyeSharp color="inherit" className="opacity-50" />
                  <span className="mx-3">{t('title_observation')}</span>
                </Link>
              </li>
            </Tooltip>
            <Tooltip title={t('title_incident')} arrow placement="right">
              <li
                className="text-start d-flex align-items-center"
                onClick={handleItemClick}
              >
                <Link
                  className={`dropdown-item px-4 menu-item-p ${
                    location.pathname?.split('/')?.[3] === 'incident'
                      ? 'active'
                      : ''
                  }`}
                  to={`/project/${projectId}/incident`}
                >
                  <WarningAmberSharp color="inherit" className="opacity-50" />
                  <span className="mx-3">{t('title_incident')}</span>
                </Link>
              </li>
            </Tooltip>
          </ul>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
